import React, { useState } from 'react';
import { CheckCircle2, ExternalLink, Loader2, Search, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { OwnerButton, OwnerCard, OwnerInfoList, OwnerShell } from '../OwnerShell';
import { TextField } from '../OwnerFields';
import { UploadTile } from '../UploadTile';
import type { UploadState } from '../UploadTile';
import { PersonPhoto } from '../../common/PersonPhoto';
import { useMyVehicle } from '../../../hooks/useMyVehicles';
import { useOwnerService } from '../../../contexts/OwnerServiceContext';
import { drivers } from '../../../data/drivers';
import { ownerProfile } from '../../../data/ownerProfile';
import type { Driver } from '../../../types/domain';
import type { OwnerNav } from '../../../types/owner';
type Step = 'license' | 'docs' | 'done';
export function OwnerAddDriver({
  nav
}: {
  nav: OwnerNav;
}) {
  const navigate = useNavigate();
  const mv = useMyVehicle(nav.vehicleId);
  const {
    submitRequest
  } = useOwnerService();
  const [step, setStep] = useState<Step>('license');
  const [license, setLicense] = useState('BRTA-8899001');
  const [searching, setSearching] = useState(false);
  const [found, setFound] = useState<Driver | null | undefined>(undefined);
  const [docs, setDocs] = useState<Record<string, UploadState>>({
    lic: 'missing',
    nid: 'missing',
    photo: 'missing',
    consent: 'missing'
  });
  const [rid, setRid] = useState('');
  if (!mv) return null;
  const v = mv.vehicle;
  const search = () => {
    setSearching(true);
    setFound(undefined);
    window.setTimeout(() => {
      const d = drivers.find(x => x.license === license.trim().toUpperCase());
      setFound(d && !mv.driverIds.includes(d.id) ? d : null);
      setSearching(false);
    }, 900);
  };
  const submit = () => {
    if (!found) return;
    if (Object.values(docs).some(s => s !== 'uploaded')) return nav.showToast('সকল ডকুমেন্ট আপলোড করুন');
    const id = submitRequest({
      kind: 'চালক সংযুক্তি',
      ownerName: ownerProfile.name,
      vehicleReg: v.reg,
      subject: 'নতুন চালক সংযুক্তি',
      current: `${mv.driverIds.length} জন অনুমোদিত চালক`,
      proposed: `${found.name} (${found.license})`,
      reason: 'অতিরিক্ত শিফটের জন্য চালক প্রয়োজন',
      vehicleId: v.id,
      driverId: found.id,
      attachments: 4
    });
    setRid(id);
    setStep('done');
  };
  return <OwnerShell nav={nav} title="চালক সংযুক্তির আবেদন" subtitle={v.reg} footer={step === 'license' ? <OwnerButton disabled={!found} onClick={() => setStep('docs')} data-id="element-1327" data-mp="2v2n93s2qkor1">পরবর্তী — ডকুমেন্ট আপলোড</OwnerButton> : step === 'docs' ? <div className="grid grid-cols-2 gap-2" data-id="element-1328" data-mp="34usweoi1mhzw">
            <OwnerButton variant="outline" onClick={() => setStep('license')} data-id="element-1329" data-mp="34thrumgrx67q">পূর্ববর্তী</OwnerButton>
            <OwnerButton variant="green" onClick={submit} data-id="element-1330" data-mp="2zvydlmk2stfs">আবেদন জমা দিন</OwnerButton>
          </div> : <OwnerButton onClick={() => nav.back()} data-id="element-1331" data-mp="3dslchuvl9toc">চালকের তালিকায় ফিরে যান</OwnerButton>} data-id="element-1326" data-mp="2h4fcjl9m3pge">
      {step === 'license' && <div className="space-y-3" data-id="element-1332" data-mp="3sheqf8ld8o99">
          <p className="text-[13px] text-slate-600" data-id="element-1333" data-mp="3sifzx8mqh2ov">চালকের ড্রাইভিং লাইসেন্স নম্বর দিন। BRTA রেকর্ড থেকে তথ্য খোঁজা হবে (সিমুলেটেড)।</p>
          <TextField id="ad-lic" label="ড্রাইভিং লাইসেন্স নম্বর" required mono value={license} onChange={x => {
        setLicense(x);
        setFound(undefined);
      }} hint="নমুনা: BRTA-8899001" data-id="element-1334" data-mp="3jgykztl2b71r" />
          <OwnerButton variant="outline" onClick={search} disabled={searching} data-id="element-1335" data-mp="3ohw3k79zi33s">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-1336" data-mp="39p2djrx364kv" /> : <Search className="h-4 w-4" data-id="element-1337" data-mp="3ei57401v5e12" />}লাইসেন্স খুঁজুন
          </OwnerButton>
          {found && <OwnerCard data-id="element-1338" data-mp="30ri9kb7u9u6k">
              <p className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-emerald-700" data-id="element-1339" data-mp="30s1tfxh1usn8"><CheckCircle2 className="h-4 w-4" data-id="element-1340" data-mp="34sviiu68adhp" />BRTA রেকর্ডে লাইসেন্স পাওয়া গেছে</p>
              <div className="mb-2 flex items-center gap-3" data-id="element-1341" data-mp="2qtc8fvtuws2r">
                <PersonPhoto src={found.photo} name={found.name} size={48} data-id="element-1342" data-mp="2vre198h345rf" />
                <div data-id="element-1343" data-mp="2vtgppo2l9qu1"><p className="font-semibold text-navy-900" data-id="element-1344" data-mp="2vtgu009mgczu">{found.name}</p><p className="font-mono text-[11px] text-slate-500" data-id="element-1345" data-mp="1ycu02vna4j5">{found.license}</p></div>
              </div>
              <OwnerInfoList rows={[['লাইসেন্সের ধরন', found.licenseType], ['মেয়াদ', found.expiry], ['মোবাইল', found.mobile]]} data-id="element-1346" data-mp="3u3qf1fz1622f" />
            </OwnerCard>}
          {found === null && <p className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-[12px] text-red-800" data-id="element-1347" data-mp="3kclwk6a5bz2u"><XCircle className="mt-0.5 h-4 w-4 shrink-0" data-id="element-1348" data-mp="3ffdd82d9hp00" />লাইসেন্স পাওয়া যায়নি অথবা চালক ইতোমধ্যে এই গাড়িতে সংযুক্ত।</p>}
        </div>}
      {step === 'docs' && <div className="space-y-2.5" data-id="element-1349" data-mp="36cuftxs8w4l5">
          <p className="text-[13px] text-slate-600" data-id="element-1350" data-mp="36dw453y9unkt"><b data-id="element-1351" data-mp="31esxjf2n89aq">{found?.name}</b> — প্রয়োজনীয় কাগজপত্র আপলোড করুন।</p>
          {[['lic', 'ড্রাইভিং লাইসেন্সের কপি'], ['nid', 'চালকের NID'], ['photo', 'চালকের সাম্প্রতিক ছবি'], ['consent', 'মালিকের সম্মতিপত্র']].map(([k, l]) => <UploadTile key={k} label={l} required state={docs[k]} fileName={`driver_${k}.jpg`} preview={k === 'photo' ? found?.photo : undefined} onUpload={() => setDocs(d => ({
        ...d,
        [k]: 'uploaded'
      }))} data-id="element-1352" data-mp="176p2ekd3g249" />)}
        </div>}
      {step === 'done' && <div className="pt-6 text-center" data-id="element-1353" data-mp="1gyle4y83rqqy">
          <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-bdgreen-600 text-white" data-id="element-1354" data-mp="1lw3bdrcrchj4"><CheckCircle2 className="h-11 w-11" data-id="element-1355" data-mp="o8mkepiyr5fy" /></span>
          <h2 className="mt-4 font-heading text-xl font-bold text-navy-900" data-id="element-1356" data-mp="o9xkofd79v2b">আবেদন জমা হয়েছে</h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-1357" data-mp="t90jhi2l751u">ডিএমপি যাচাই ও অনুমোদনের পর চালক তালিকায় যুক্ত হবেন।</p>
          <p className="mx-auto mt-4 w-fit rounded-lg bg-slate-100 px-4 py-2 font-mono text-base font-bold text-navy-900" data-id="element-1358" data-mp="y8dckkk1x8cv">{rid}</p>
          <button onClick={() => navigate('/admin/owner/requests')} className="mx-auto mt-4 flex items-center gap-1.5 text-[11.5px] font-medium text-navy-700" data-id="element-1359" data-mp="29lxp0b4wia4f">
            <ExternalLink className="h-3.5 w-3.5" data-id="element-1360" data-mp="2dolyml1fvl2g" />প্রোটোটাইপ: ওয়েব অ্যাডমিনে অনুমোদন করুন
          </button>
        </div>}
    </OwnerShell>;
}